import { db } from "config/firebase";
import { createHash } from "utils/createHash";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";

interface StartGameSessionParams {
  hostId: string;
  gameId: string;
  gameName: string;
  startedAt: number;
  gameSessionId: string;
}

export const startGameSession = async ({
  hostId,
  gameId,
  gameName,
  startedAt,
  gameSessionId,
}: StartGameSessionParams): Promise<void> => {
  console.log("start game session");
  try {
    await setDoc(doc(db, "gameSessions", gameSessionId), {
      id: gameSessionId,
      gameId,
      gameName,
      hostId,
      startedAt,
      endedAt: null,
    });
  } catch (err) {
    console.log("Error starting game Session", err);
    throw err;
  }
};

interface EndGameSessionParams {
  gameId: string;
  gameSessionId: string | null | undefined;
}

export const endGameSession = async ({
  gameId: _gameId,
  gameSessionId,
}: EndGameSessionParams): Promise<void> => {
  console.log("end game session1");

  // Check if gameSessionId is valid
  if (!gameSessionId || gameSessionId === undefined || gameSessionId === null) {
    console.log(
      "No active game session to end - gameSessionId is invalid:",
      gameSessionId,
    );
    return; // Exit gracefully if no session to end
  }

  try {
    await updateDoc(doc(db, "gameSessions", gameSessionId), {
      endedAt: Date.now(),
    });
    console.log("Game session ended successfully:", gameSessionId);
  } catch (err) {
    console.log("Error ending game Session", err);
    throw err;
  }
};

async function _createActiveSessionId(
  gameId: string,
): Promise<{ gameSessionId: string; startedAt: number }> {
  try {
    const gameRef = doc(db, "games", gameId);
    const docSnap = await getDoc(gameRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const { startedAt } = data;
      if (!startedAt) {
        throw new Error("Not active session");
      } else {
        const gameSessionId = createHash(gameId + startedAt);
        return { gameSessionId, startedAt };
      }
    } else {
      throw new Error("Game not found");
    }
  } catch (err) {
    console.log(`Error creating session id:`, err);
    throw err;
  }
}

interface StartPlayerSessionParams {
  gameSessionId?: string | null;
  reqId?: string | null;
  hostId: string;
  gameId: string;
  gameName: string;
  playerId: string;
  playerName: string;
}

export const startPlayerSession = async ({
  gameSessionId = null,
  reqId = null,
  hostId,
  gameId,
  gameName,
  playerId,
  playerName,
}: StartPlayerSessionParams): Promise<void> => {
  try {
    const startedAt = Date.now();
    let finalGameSessionId = gameSessionId;
    if (!finalGameSessionId) {
      const gameRef = doc(db, "games", gameId);
      const docSnap = await getDoc(gameRef);
      if (docSnap.exists()) {
        const gameDoc = docSnap.data();
        finalGameSessionId = gameDoc.gameSessionId;
      }
    }
    console.log(`gameSessionId`, finalGameSessionId);

    const playerSessionId = createHash(playerId + startedAt);
    await setDoc(doc(db, "playerSessions", playerSessionId), {
      id: playerSessionId,
      reqId,
      gameSessionId: finalGameSessionId,
      gameId,
      gameName,
      hostId,
      playerId,
      playerName,
      startedAt,
      endedAt: null,
    });
  } catch (err) {
    console.log("Error creating player session", err);
    throw err;
  }
};

interface EndPlayerSessionParams {
  gameSessionId?: string | null;
  gameId: string;
  playerId: string;
}

export const endPlayerSession = async ({
  gameSessionId = null,
  gameId,
  playerId,
}: EndPlayerSessionParams): Promise<void> => {
  try {
    let finalGameSessionId = gameSessionId;
    if (!finalGameSessionId) {
      const gameRef = doc(db, "games", gameId);
      const docSnap = await getDoc(gameRef);
      if (docSnap.exists()) {
        const gameDoc = docSnap.data();
        finalGameSessionId = gameDoc.gameSessionId;
      }
    }
    console.log(`gameSessionId`, finalGameSessionId);

    // Check if finalGameSessionId is valid before querying
    if (!finalGameSessionId || typeof finalGameSessionId !== 'string' || finalGameSessionId.trim() === '') {
      console.log(
        "No valid game session ID found - cannot end player session:",
        finalGameSessionId,
        "Type:", typeof finalGameSessionId
      );
      return; // Exit gracefully if no valid session ID
    }

    // should only return 1 document max
    const playerSessionQuery = query(
      collection(db, "playerSessions"),
      where("gameSessionId", "==", finalGameSessionId),
      where("playerId", "==", playerId),
    );
    const playerSessionDocs = await getDocs(playerSessionQuery);

    if (!playerSessionDocs.empty) {
      const playerSessionId = playerSessionDocs.docs[0].id;
      await updateDoc(doc(db, "playerSessions", playerSessionId), {
        endedAt: Date.now(),
      });
    }
  } catch (err) {
    console.log("Error ending player session", err);
    throw err;
  }
};

interface GetPlayerSessionsParams {
  playerId?: string | null;
  gameId?: string | null;
  startTime?: number | null;
  endTime?: number | null;
  hostId?: string | null;
}

export const getPlayerSessions = async ({
  playerId = null, //! either playerId or hostId must be defined
  gameId = null,
  startTime = null,
  endTime = null,
  hostId = null,
}: GetPlayerSessionsParams): Promise<any[]> => {
  try {
    let sessionQuery;
    const baseCollection = collection(db, "playerSessions");

    if (hostId) {
      sessionQuery = query(baseCollection, where("hostId", "==", hostId));
    } else if (playerId) {
      sessionQuery = query(baseCollection, where("playerId", "==", playerId));
    } else {
      throw new Error("Either hostId or playerId must be defined");
    }

    // Apply additional filters
    const constraints = [];
    if (startTime) constraints.push(where("startedAt", ">", startTime));
    if (endTime) constraints.push(where("startedAt", "<", endTime));
    if (gameId) constraints.push(where("gameId", "==", gameId));

    if (constraints.length > 0) {
      sessionQuery = query(sessionQuery, ...constraints);
    }

    console.log(`query`, sessionQuery);

    const playerSessions = await getDocs(sessionQuery);

    const sessions: any[] = [];
    playerSessions.forEach((session) => {
      sessions.push(session.data());
    });
    return sessions;
  } catch (err) {
    console.log("Error querying player sessions", err);
    throw new Error(err as string);
  }
};

interface GetGameSessionsParams {
  hostId: string;
  gameSessionId?: string | null;
  startTime?: number | null;
  endTime?: number | null;
}

export const getGameSessions = async ({
  hostId,
  gameSessionId = null,
  startTime = null,
  endTime = null,
}: GetGameSessionsParams): Promise<any[]> => {
  try {
    const baseCollection = collection(db, "gameSessions");
    const constraints = [where("hostId", "==", hostId)];

    if (startTime) constraints.push(where("startedAt", ">", startTime));
    if (endTime) constraints.push(where("startedAt", "<", endTime));
    if (gameSessionId) constraints.push(where("id", "==", gameSessionId));

    const sessionQuery = query(baseCollection, ...constraints);
    const hostSessions = await getDocs(sessionQuery);

    const sessions: any[] = [];
    hostSessions.forEach((session) => sessions.push(session.data()));
    return sessions;
  } catch (err) {
    console.log("Error querying host sessions", err);
    throw err;
  }
};

export default {
  startGameSession,
  endGameSession,
  startPlayerSession,
  endPlayerSession,
  getPlayerSessions,
  getGameSessions,
};
